import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ListItemText,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    CircularProgress,
    makeStyles,
    withStyles,
} from '@material-ui/core';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import CheckIcon from '@material-ui/icons/Check';
import { connect, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import moment from 'moment';
import XLSX from 'xlsx';
import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import { setProductionSummaryProjects } from '../../redux/actions/productionSummaryProjectAction';
import PanelDownloadProgressDialog from '../../components/PanelDownloadProgressDialog';
import { downloadProductionSummaryPanelCsv, saveCsvBlob } from '../../utils/panelDownload';
import { ROLENAME } from '../../utils';

const NAVY = '#1C375D';
const ORANGE = '#F47E46';
const GREEN = '#355025';
const FETCH_NEW_DATA_COOLDOWN_MS = 3 * 60 * 1000;
const FETCH_NEW_DATA_COOLDOWN_SECONDS = 180;
const FETCH_NEW_DATA_DISABLE_UNTIL_KEY = 'productionSummaryProject_fetchNewData_disableUntil';
const PANEL_PREVIEW_LIMIT = 500;

const SEARCH_TYPES = ['Project No', 'Unique Lead No', 'Customer Name'];
const ALL_CITIES = 'All Cities';
const ALL_STAGES = 'All Stages';
const ALL_PAYMENT_STATUSES = 'All Payment Status';
const PAYMENT_STATUS = {
    NOT_PAID: 'Not Paid',
    PARTIAL: 'Partially Paid',
    PAID: 'Paid',
};
const PAYMENT_STATUS_OPTIONS = [
    PAYMENT_STATUS.NOT_PAID,
    PAYMENT_STATUS.PARTIAL,
    PAYMENT_STATUS.PAID,
];
const TABLE_META_COLUMNS = 8;
const SUMMARY_EXTRA_COLUMNS = 3;
const MATERIAL_READINESS_MIN_DATE = moment().add(1, 'day').format('YYYY-MM-DD');
const STICKY_SN_WIDTH = 56;
const STICKY_PROJECT_LEFT = STICKY_SN_WIDTH;
const STICKY_PROJECT_WIDTH = 140;

const formatMultiSelectLabel = (selected, allLabel) => {
    if (!selected.length) return allLabel;
    if (selected.length <= 2) return selected.join(', ');
    return `${selected.length} selected`;
};

const resolveDateRange = (startDate, endDate) => ({
    startDate: startDate || '',
    endDate: endDate || '',
});

const formatDisplayDate = (value, emptyLabel = '—') => {
    if (!value) return emptyLabel;
    const formatted = moment(value);
    return formatted.isValid() ? formatted.format('DD-MM-YYYY') : emptyLabel;
};

const getRfdDaysDifference = (row) => {
    if (row?.rfdDaysDiff != null && row.rfdDaysDiff !== '' && !Number.isNaN(Number(row.rfdDaysDiff))) {
        return Number(row.rfdDaysDiff);
    }
    if (row?.firstBeamScanDate && row?.readyToDispatchDate) {
        const start = moment(row.firstBeamScanDate).startOf('day');
        const end = moment(row.readyToDispatchDate).startOf('day');
        if (start.isValid() && end.isValid()) {
            return end.diff(start, 'days');
        }
    }
    return null;
};

const formatRfdDaysDifference = (row, emptyLabel = '—') => {
    const days = getRfdDaysDifference(row);
    return days == null ? emptyLabel : days;
};

const getRfdDaysDifferenceTone = (days) => {
    if (days == null || Number.isNaN(Number(days))) return null;
    const value = Number(days);
    if (value < 6) return 'green';
    if (value <= 8) return 'orange';
    return 'red';
};

const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return `₹${num.toLocaleString('en-IN')}`;
};

const getReceivedPercentage = (row) => {
    if (row?.receivedPercentage != null && !Number.isNaN(Number(row.receivedPercentage))) {
        return Number(row.receivedPercentage);
    }
    const received = Number(row?.totalAmountReceived) || 0;
    const base = Number(row?.staticDesignWonAmount) || 0;
    if (!base) return 0;
    return Number(((received / base) * 100).toFixed(2));
};

const getPaymentStatus = (row) => {
    const pct = getReceivedPercentage(row);
    if (pct >= 100) return PAYMENT_STATUS.PAID;
    if (pct >= 70.1) return PAYMENT_STATUS.PARTIAL;
    return PAYMENT_STATUS.NOT_PAID;
};

const isMaterialReadinessLocked = (row) => Boolean(row?.materialReadinessDate) || row?.isEditable === false;

const formatUnitNames = (row) => {
    const unitWise = row?.unitWise;
    if (unitWise && typeof unitWise === 'object' && Object.keys(unitWise).length) {
        return Object.entries(unitWise)
            .sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0))
            .map(([unitName]) => unitName)
            .join(', ');
    }
    if (row?.mostScannedUnit) {
        return row.mostScannedUnit;
    }
    return 'NA';
};

const formatUnitWiseTooltip = (row) => {
    const unitWise = row?.unitWise;
    if (!unitWise || typeof unitWise !== 'object' || !Object.keys(unitWise).length) {
        return null;
    }
    return Object.entries(unitWise)
        .sort((a, b) => (Number(b[1]) || 0) - (Number(a[1]) || 0))
        .map(([unitName, count]) => `${unitName}: ${Number(count || 0).toLocaleString('en-IN')}`)
        .join('\n');
};

const CURRENT_STAGE_LABELS = {
    READY_FOR_DISPATCH: 'Ready for Dispatch',
    UNDER_PRODUCTION_FIXING: 'Under Production - Fixing',
    UNDER_PRODUCTION_MACHINE: 'Under Production - Machine',
};

const CURRENT_STAGE_ORDER = [
    CURRENT_STAGE_LABELS.READY_FOR_DISPATCH,
    CURRENT_STAGE_LABELS.UNDER_PRODUCTION_FIXING,
    CURRENT_STAGE_LABELS.UNDER_PRODUCTION_MACHINE,
];

const normalizeCurrentStage = (value) => {
    const text = String(value || '').trim();
    if (!text) return '';
    if (/ready\s*for\s*dispatch/i.test(text)) return CURRENT_STAGE_LABELS.READY_FOR_DISPATCH;
    if (/under\s*production\s*[-–]?\s*fixing/i.test(text)) return CURRENT_STAGE_LABELS.UNDER_PRODUCTION_FIXING;
    if (/under\s*production\s*[-–]?\s*machine/i.test(text)) return CURRENT_STAGE_LABELS.UNDER_PRODUCTION_MACHINE;
    return text;
};

const getCurrentStageSortRank = (value) => {
    const normalized = normalizeCurrentStage(value);
    const index = CURRENT_STAGE_ORDER.indexOf(normalized);
    return index >= 0 ? index : CURRENT_STAGE_ORDER.length;
};

const sortRowsByStageAndCity = (data = []) => [...data].sort((a, b) => {
    const stageCompare = getCurrentStageSortRank(a.currentStage) - getCurrentStageSortRank(b.currentStage);
    if (stageCompare !== 0) {
        return stageCompare;
    }

    const cityCompare = (a.cityName || '').localeCompare(b.cityName || '', undefined, {
        sensitivity: 'base',
        numeric: true,
    });
    if (cityCompare !== 0) {
        return cityCompare;
    }

    return (a.uniqueLeadNumber || '').localeCompare(b.uniqueLeadNumber || '', undefined, {
        sensitivity: 'base',
        numeric: true,
    });
});

const STAGE_COLUMNS = [
    { key: 'beamSaw', label: 'Beam Saw', stage: 'Beam saw', getCount: (row) => stageValue(row, 'Beam saw') },
    { key: 'edgeBanding', label: 'Edge Banding', stage: 'Edge banding', getCount: (row) => stageValue(row, 'Edge banding') },
    { key: 'cnc', label: 'CNC', stage: 'CNC', getCount: (row) => stageValue(row, 'CNC') },
    { key: 'aluPanel', label: 'Alu Panel', stage: 'Alu Panels', getCount: (row) => stageValue(row, 'Alu Panels') },
    { key: 'cncJali', label: 'CNC Jali', stage: 'CNC Jali', getCount: (row) => stageValue(row, 'CNC Jali') },
    { key: 'customPanel', label: 'Custom Panel', stage: 'Custom Panels', getCount: (row) => stageValue(row, 'Custom Panels') },
    { key: 'qc', label: 'QC', stage: 'Assembly', getCount: (row) => stageValue(row, 'Assembly') },
    { key: 'packaging', label: 'Packaging', stage: 'Packaging', getCount: (row) => stageValue(row, 'Packaging') },
    { key: 'readyForDispatch', label: 'Ready for Dispatch', stage: 'Completed', getCount: (row) => stageValue(row, 'Completed') },
    { key: 'deliveredProductPanel', label: 'Delivered', stage: 'Delivered', getCount: (row) => stageValue(row, 'Delivered') },
    { key: 'totalPanels', label: 'Total Panels', stage: 'Total', getCount: (row) => Number(row.totalPanels || 0) },
];

const PANEL_COLUMNS = [
    { key: 'sn', label: 'SN' },
    { key: 'barcode', label: 'Barcode' },
    { key: 'name1', label: 'Panel Name' },
    { key: 'matname', label: 'Material' },
    { key: 'cp90name', label: 'Item' },
    { key: 'stage', label: 'Stage' },
    { key: 'space_type', label: 'Space Type' },
    { key: 'rleng', label: 'Length' },
    { key: 'rwidth', label: 'Width' },
    { key: 'rthk', label: 'Thickness' },
];

const StyledTableCell = withStyles(() => ({
    head: {
        backgroundColor: NAVY,
        color: '#FFF2EB',
        fontWeight: 600,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        border: '1px solid #eee5e5',
        padding: '10px 12px',
        fontSize: 13,
    },
    body: {
        fontSize: 13,
        color: NAVY,
        textAlign: 'center',
        border: '1px solid #eee5e5',
        padding: '8px 12px',
        backgroundColor: '#fff',
    },
}))(TableCell);

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(2) },
    pageHeader: {
        marginBottom: theme.spacing(1.5),
    },
    title: {
        color: NAVY,
        fontWeight: 700,
        marginBottom: theme.spacing(0.5),
    },
    subtitle: {
        color: '#5c6b7a',
    },
    filterPaper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        border: '1px solid #e8edf3',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: '#fafbfc',
    },
    filterRowTop: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
        gap: theme.spacing(1.5),
        alignItems: 'end',
    },
    topRowItem: {
        width: '100%',
        minWidth: 0,
    },
    filterRowSecondary: {
        marginTop: theme.spacing(1.5),
        paddingTop: theme.spacing(1.5),
        borderTop: '1px solid #e8edf3',
    },
    filterField: {
        '& .MuiOutlinedInput-root': {
            minHeight: 40,
            backgroundColor: '#fff',
        },
        '& .MuiInputLabel-outlined': {
            backgroundColor: '#fafbfc',
            padding: '0 4px',
        },
        '& .MuiSelect-select': {
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box',
        },
    },
    searchTypeField: {
        flex: '0 0 170px',
        minWidth: 170,
    },
    search: {
        flex: '1 1 280px',
        minWidth: 220,
        maxWidth: 420,
    },
    searchRowGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: theme.spacing(1.5),
    },
    filterActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexShrink: 0,
    },
    totalProjects: {
        fontSize: 13,
        color: '#5c6b7a',
        backgroundColor: '#fff',
        padding: theme.spacing(0.75, 1.5),
        borderRadius: 16,
        border: '1px solid #e8edf3',
        height: 40,
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
    },
    actionBtn: {
        backgroundColor: ORANGE,
        color: '#fff',
        fontWeight: 600,
        textTransform: 'none',
        height: 40,
        minWidth: 96,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#E0663D',
            boxShadow: 'none',
        },
    },
    disabledActionBtn: {
        backgroundColor: '#cccccc',
        color: '#fff',
        fontWeight: 600,
        textTransform: 'none',
        height: 40,
        minWidth: 96,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#cccccc',
            boxShadow: 'none',
        },
    },
    downloadBtn: {
        backgroundColor: GREEN,
        color: '#FFF2EB',
        fontWeight: 600,
        textTransform: 'none',
        height: 40,
        minWidth: 130,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#2a401e',
            boxShadow: 'none',
        },
    },
    topRowDownloadBtn: {
        width: '100%',
        minWidth: 0,
    },
    tableContainer: {
        marginTop: theme.spacing(1),
        overflowX: 'auto',
        maxWidth: '100%',
    },
    table: {
        minWidth: 1100,
        borderCollapse: 'separate',
        borderSpacing: 0,
    },
    stickySnHead: {
        position: 'sticky',
        left: 0,
        zIndex: 5,
        minWidth: STICKY_SN_WIDTH,
        width: STICKY_SN_WIDTH,
        backgroundColor: NAVY,
        boxShadow: '2px 0 4px -2px rgba(28, 55, 93, 0.2)',
    },
    stickyProjectHead: {
        position: 'sticky',
        left: STICKY_PROJECT_LEFT,
        zIndex: 5,
        minWidth: STICKY_PROJECT_WIDTH,
        backgroundColor: NAVY,
        boxShadow: '2px 0 4px -2px rgba(28, 55, 93, 0.2)',
    },
    stickySnCell: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        minWidth: STICKY_SN_WIDTH,
        width: STICKY_SN_WIDTH,
        backgroundColor: '#fff',
        boxShadow: '2px 0 4px -2px rgba(0, 0, 0, 0.08)',
    },
    stickyProjectCell: {
        position: 'sticky',
        left: STICKY_PROJECT_LEFT,
        zIndex: 3,
        minWidth: STICKY_PROJECT_WIDTH,
        backgroundColor: '#fff',
        boxShadow: '2px 0 4px -2px rgba(0, 0, 0, 0.08)',
    },
    projectNoLink: {
        color: NAVY,
        fontWeight: 600,
        textDecoration: 'underline',
        '&:hover': {
            color: ORANGE,
        },
    },
    stickySnCellTotal: {
        backgroundColor: '#f5f7fa',
    },
    stickyProjectCellTotal: {
        backgroundColor: '#f5f7fa',
    },
    totalRow: {
        '& td': {
            fontWeight: 700,
            backgroundColor: '#f5f7fa',
        },
    },
    clickableCount: {
        color: NAVY,
        fontWeight: 700,
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:hover': {
            color: ORANGE,
        },
    },
    disabledCount: {
        color: '#9e9e9e',
    },
    dialogTitle: {
        backgroundColor: NAVY,
        color: '#fff',
    },
    dialogMeta: {
        marginBottom: theme.spacing(1.5),
        color: NAVY,
    },
    headerHint: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        cursor: 'help',
    },
    helpMark: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 16,
        height: 16,
        borderRadius: '50%',
        border: '1px solid #FFF2EB',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
    },
    paymentStatusCell: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.75),
    },
    paymentStatusChip: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 96,
        padding: '4px 10px',
        borderRadius: 14,
        fontSize: 12,
        fontWeight: 600,
        color: '#fff',
        whiteSpace: 'nowrap',
    },
    paymentStatusNotPaid: {
        backgroundColor: '#d32f2f',
    },
    paymentStatusPartial: {
        backgroundColor: ORANGE,
    },
    paymentStatusPaid: {
        backgroundColor: GREEN,
    },
    daysDifferenceCell: {
        fontWeight: 700,
    },
    daysDifferenceGreen: {
        color: '#1b7a1b !important',
        backgroundColor: '#e8f7e8 !important',
    },
    daysDifferenceOrange: {
        color: '#c65d1a !important',
        backgroundColor: '#fff0e0 !important',
    },
    daysDifferenceRed: {
        color: '#c62828 !important',
        backgroundColor: '#fdeaea !important',
    },
    paymentViewIcon: {
        color: NAVY,
        cursor: 'pointer',
        fontSize: 18,
        '&:hover': {
            color: ORANGE,
        },
    },
    paymentTooltipLine: {
        fontSize: 13,
        lineHeight: 1.5,
    },
    materialReadinessCell: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        flexWrap: 'nowrap',
        width: '100%',
        minWidth: 210,
    },
    materialReadinessDateField: {
        flex: '1 1 auto',
        minWidth: 128,
        maxWidth: 142,
        '& .MuiOutlinedInput-root': {
            minHeight: 36,
            backgroundColor: '#fff',
            borderRadius: 6,
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 10px',
            fontSize: 12,
        },
    },
    materialReadinessSubmitBtn: {
        flexShrink: 0,
        minWidth: 78,
        height: 36,
        padding: theme.spacing(0.75, 1.5),
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: 6,
        boxShadow: 'none',
        backgroundColor: ORANGE,
        color: '#fff',
        '&:hover': {
            backgroundColor: '#E0663D',
            boxShadow: 'none',
        },
        '&.Mui-disabled': {
            backgroundColor: '#f3f4f6',
            color: '#9aa5b1',
            border: '1px solid #dde3ea',
        },
    },
    materialReadinessSavedDate: {
        fontWeight: 600,
        color: NAVY,
        whiteSpace: 'nowrap',
    },
}));

const stageValue = (row, key) => Number(row?.stageWise?.[key] || 0);

const formatPanelStageDisplay = (stage, panel) => {
    if (panel?.isDelivered) return 'Delivered';
    if (!stage) return '';
    if (stage === 'Assembly') return 'QC';
    if (stage === 'Completed') return 'Ready for Dispatch';
    return stage;
};

const ProductionSummaryProject = (props) => {
    const classes = useStyles();
    const [allRows, setAllRows] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [selectedCities, setSelectedCities] = useState([ALL_CITIES]);
    const [selectedStages, setSelectedStages] = useState([ALL_STAGES]);
    const [selectedPaymentStatuses, setSelectedPaymentStatuses] = useState([ALL_PAYMENT_STATUSES]);
    const [search, setSearch] = useState('');
    const [searchType, setSearchType] = useState('Project No');
    const [activeSearch, setActiveSearch] = useState('');
    const [activeSearchType, setActiveSearchType] = useState('');
    const [createdStartDate, setCreatedStartDate] = useState('');
    const [createdEndDate, setCreatedEndDate] = useState('');
    const [appliedCreatedStartDate, setAppliedCreatedStartDate] = useState('');
    const [appliedCreatedEndDate, setAppliedCreatedEndDate] = useState('');
    const [isFetchDisabled, setIsFetchDisabled] = useState(false);
    const [fetchTimeLeft, setFetchTimeLeft] = useState(0);
    const searchRequestId = useRef(0);
    const initialLoadDone = useRef(false);
    const isPageReload = useMemo(
        () => performance.getEntriesByType('navigation')[0]?.type === 'reload',
        []
    );
    const isStoreRehydrated = useSelector((state) => state._persist?.rehydrated);
    const [panelDialogOpen, setPanelDialogOpen] = useState(false);
    const [panelDownloadOpen, setPanelDownloadOpen] = useState(false);
    const [panelDownloadProgress, setPanelDownloadProgress] = useState(0);
    const [panelRows, setPanelRows] = useState([]);
    const [panelMeta, setPanelMeta] = useState({
        projectNo: '',
        stageLabel: '',
        stage: '',
        count: 0,
        previewCount: 0,
        isPreview: false,
        projectId: '',
        projectIds: [],
    });
    const [materialReadinessDrafts, setMaterialReadinessDrafts] = useState({});
    const [materialReadinessSubmitting, setMaterialReadinessSubmitting] = useState({});

    const isFactoryManager = useMemo(
        () => (props.userDetails?.roles || []).some((role) => role.name === ROLENAME.FactoryManager),
        [props.userDetails]
    );

    const fetchData = async ({
        nextSearchType = '',
        nextSearch = '',
        nextCreatedStartDate = appliedCreatedStartDate,
        nextCreatedEndDate = appliedCreatedEndDate,
        updateCache = false,
        showLoader = true,
    } = {}) => {
        const requestId = ++searchRequestId.current;
        if (showLoader) {
            props.setLoading(true);
        }
        try {
            const res = await HttpService.getProductionSummaryProjects({
                searchType: nextSearchType,
                search: nextSearch,
                createdStartDate: nextCreatedStartDate,
                createdEndDate: nextCreatedEndDate,
            });
            if (requestId !== searchRequestId.current) return;
            const data = sortRowsByStageAndCity((res.data?.msg || []).map((row) => ({
                ...row,
                currentStage: normalizeCurrentStage(row.currentStage),
            })));
            const cities = (res.data?.cities || [...new Set(data.map((row) => row.cityName).filter(Boolean))])
                .slice()
                .sort((a, b) => a.localeCompare(b, undefined, {
                    sensitivity: 'base',
                    numeric: true,
                }));
            setAllRows(data);
            setCityOptions(cities);
            if (updateCache) {
                props.setProductionSummaryProjects({ projects: data, cityOptions: cities });
            }
        } catch (error) {
            if (requestId !== searchRequestId.current) return;
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data?.error || 'Failed to load production summary',
                'error'
            );
        } finally {
            if (showLoader && requestId === searchRequestId.current) {
                props.setLoading(false);
            }
        }
    };

    const rows = useMemo(() => {
        let filtered = allRows;

        if (selectedCities.length && !selectedCities.includes(ALL_CITIES)) {
            filtered = filtered.filter((row) => selectedCities.includes(row.cityName));
        }

        if (selectedStages.length && !selectedStages.includes(ALL_STAGES)) {
            filtered = filtered.filter((row) => selectedStages.includes(row.currentStage));
        }

        if (selectedPaymentStatuses.length && !selectedPaymentStatuses.includes(ALL_PAYMENT_STATUSES)) {
            filtered = filtered.filter((row) => selectedPaymentStatuses.includes(getPaymentStatus(row)));
        }

        return filtered;
    }, [allRows, selectedCities, selectedStages, selectedPaymentStatuses]);

    useEffect(() => {
        if (!isStoreRehydrated || initialLoadDone.current) return;

        initialLoadDone.current = true;

        const hasCachedData = props.isProductionSummaryLoaded && props.productionSummaryProjects.length > 0;

        if (hasCachedData) {
            setAllRows(props.productionSummaryProjects);
            setCityOptions(props.productionSummaryCityOptions);
        }

        // Only show loader when there is no Redux cache yet.
        // On browser reload with cache, refresh quietly so table stays usable.
        if (!hasCachedData) {
            fetchData({ nextSearchType: '', nextSearch: '', updateCache: true, showLoader: true });
        } else if (isPageReload) {
            fetchData({ nextSearchType: '', nextSearch: '', updateCache: true, showLoader: false });
        }
    }, [isStoreRehydrated]);

    useEffect(() => {
        const disableUntilTimestamp = localStorage.getItem(FETCH_NEW_DATA_DISABLE_UNTIL_KEY);

        if (disableUntilTimestamp) {
            const now = Date.now();
            const disableUntil = parseInt(disableUntilTimestamp, 10);

            if (disableUntil > now) {
                const remainingTimeInSeconds = Math.ceil((disableUntil - now) / 1000);
                setFetchTimeLeft(remainingTimeInSeconds);
                setIsFetchDisabled(true);
            } else {
                localStorage.removeItem(FETCH_NEW_DATA_DISABLE_UNTIL_KEY);
                setIsFetchDisabled(false);
                setFetchTimeLeft(0);
            }
        }

        let timer;
        if (isFetchDisabled && fetchTimeLeft > 0) {
            timer = setInterval(() => {
                setFetchTimeLeft((prevTime) => {
                    const newTime = prevTime - 1;
                    if (newTime <= 0) {
                        localStorage.removeItem(FETCH_NEW_DATA_DISABLE_UNTIL_KEY);
                        setIsFetchDisabled(false);
                        return 0;
                    }
                    return newTime;
                });
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [isFetchDisabled, fetchTimeLeft]);

    const formatFetchTimeLeft = () => {
        const minutes = Math.floor(fetchTimeLeft / 60);
        const seconds = fetchTimeLeft % 60;
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const startFetchNewDataCooldown = () => {
        const disableUntilTimestamp = Date.now() + FETCH_NEW_DATA_COOLDOWN_MS;
        localStorage.setItem(FETCH_NEW_DATA_DISABLE_UNTIL_KEY, disableUntilTimestamp.toString());
        setIsFetchDisabled(true);
        setFetchTimeLeft(FETCH_NEW_DATA_COOLDOWN_SECONDS);
    };

    const handleSearchSubmit = () => {
        const searchText = search.trim();
        const createdRange = resolveDateRange(createdStartDate, createdEndDate);

        if (createdRange.startDate && createdRange.endDate
            && moment(createdRange.endDate).isBefore(moment(createdRange.startDate))) {
            props.setSnackInfo('Project created end date cannot be before start date', 'warning');
            return;
        }

        setCreatedStartDate(createdRange.startDate);
        setCreatedEndDate(createdRange.endDate);
        setAppliedCreatedStartDate(createdRange.startDate);
        setAppliedCreatedEndDate(createdRange.endDate);
        setActiveSearch(searchText);
        setActiveSearchType(searchType);
        fetchData({
            nextSearchType: searchText ? searchType : '',
            nextSearch: searchText,
            nextCreatedStartDate: createdRange.startDate,
            nextCreatedEndDate: createdRange.endDate,
            updateCache: !searchText,
        });
    };

    const handleClearFilter = () => {
        setSearch('');
        setSearchType('Project No');
        setSelectedCities([ALL_CITIES]);
        setSelectedStages([ALL_STAGES]);
        setSelectedPaymentStatuses([ALL_PAYMENT_STATUSES]);
        setCreatedStartDate('');
        setCreatedEndDate('');
        setAppliedCreatedStartDate('');
        setAppliedCreatedEndDate('');
        setActiveSearch('');
        setActiveSearchType('');
        fetchData({
            nextSearchType: '',
            nextSearch: '',
            nextCreatedStartDate: '',
            nextCreatedEndDate: '',
            updateCache: true,
        });
    };

    const handleFetchNewData = () => {
        if (isFetchDisabled) return;

        setSearch('');
        setSearchType('Project No');
        setSelectedCities([ALL_CITIES]);
        setSelectedStages([ALL_STAGES]);
        setSelectedPaymentStatuses([ALL_PAYMENT_STATUSES]);
        setCreatedStartDate('');
        setCreatedEndDate('');
        setAppliedCreatedStartDate('');
        setAppliedCreatedEndDate('');
        setActiveSearch('');
        setActiveSearchType('');
        startFetchNewDataCooldown();
        fetchData({
            nextSearchType: '',
            nextSearch: '',
            nextCreatedStartDate: '',
            nextCreatedEndDate: '',
            updateCache: true,
        });
    };

    const handleCityChange = (event) => {
        const value = event.target.value;

        if (value[value.length - 1] === ALL_CITIES) {
            setSelectedCities([ALL_CITIES]);
            return;
        }

        const specificCities = value.filter((city) => city !== ALL_CITIES);

        if (!specificCities.length) {
            setSelectedCities([ALL_CITIES]);
            return;
        }

        setSelectedCities(specificCities);
    };

    const handleCurrentStageChange = (event) => {
        const value = event.target.value;

        if (value[value.length - 1] === ALL_STAGES) {
            setSelectedStages([ALL_STAGES]);
            return;
        }

        const specificStages = value.filter((stage) => stage !== ALL_STAGES);

        if (!specificStages.length) {
            setSelectedStages([ALL_STAGES]);
            return;
        }

        setSelectedStages(specificStages);
    };

    const handlePaymentStatusChange = (event) => {
        const value = event.target.value;

        if (value[value.length - 1] === ALL_PAYMENT_STATUSES) {
            setSelectedPaymentStatuses([ALL_PAYMENT_STATUSES]);
            return;
        }

        const specificStatuses = value.filter((status) => status !== ALL_PAYMENT_STATUSES);

        if (!specificStatuses.length) {
            setSelectedPaymentStatuses([ALL_PAYMENT_STATUSES]);
            return;
        }

        setSelectedPaymentStatuses(specificStatuses);
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleSearchSubmit();
        }
    };

    const totals = rows.reduce((acc, row) => {
        STAGE_COLUMNS.forEach((col) => {
            acc[col.key] += col.getCount(row);
        });
        return acc;
    }, STAGE_COLUMNS.reduce((acc, col) => {
        acc[col.key] = 0;
        return acc;
    }, {}));

    const openPanelDetails = async (row, column) => {
        const count = column.getCount(row);
        if (!count || !row._id) return;

        props.setLoading(true);
        try {
            const res = await HttpService.getProductionSummaryPanelDetails({
                projectId: row._id,
                projectNo: row.project_no || row.uniqueLeadNumber,
                stage: column.stage,
                count,
                limit: PANEL_PREVIEW_LIMIT,
            });
            const panels = res.data?.msg || [];
            setPanelRows(panels);
            setPanelMeta({
                projectNo: row.project_no || row.uniqueLeadNumber,
                stageLabel: column.label,
                stage: column.stage,
                count: res.data?.count != null ? res.data.count : panels.length,
                previewCount: res.data?.previewCount != null ? res.data.previewCount : panels.length,
                isPreview: !!res.data?.isPreview,
                projectId: row._id,
                projectIds: [],
            });
            setPanelDialogOpen(true);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data?.error || 'Failed to load panel details',
                'error'
            );
        }
        props.setLoading(false);
    };

    const openTotalPanelDetails = async (column) => {
        const count = totals[column.key];
        const trackedRows = rows.filter((row) => row._id);
        if (!count || !trackedRows.length) return;

        props.setLoading(true);
        try {
            const res = await HttpService.getProductionSummaryPanelDetails({
                projectIds: trackedRows.map((row) => row._id),
                stage: column.stage,
                count,
                limit: PANEL_PREVIEW_LIMIT,
            });
            const panels = res.data?.msg || [];
            setPanelRows(panels);
            setPanelMeta({
                projectNo: `All Projects (${trackedRows.length})`,
                stageLabel: column.label,
                stage: column.stage,
                count: res.data?.count != null ? res.data.count : panels.length,
                previewCount: res.data?.previewCount != null ? res.data.previewCount : panels.length,
                isPreview: !!res.data?.isPreview,
                projectId: '',
                projectIds: trackedRows.map((row) => row._id),
            });
            setPanelDialogOpen(true);
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data?.error || 'Failed to load panel details',
                'error'
            );
        }
        props.setLoading(false);
    };

    const downloadSummaryCsv = () => {
        if (!rows.length) {
            props.setSnackInfo('No data to download', 'warning');
            return;
        }

        const csvRows = rows.map((row, index) => ({
            SN: index + 1,
            'Project No': row.uniqueLeadNumber || row.project_no || '',
            'Created Date': formatDisplayDate(row.createdAt, 'NA'),
            'First Beam Scan Date': formatDisplayDate(row.firstBeamScanDate, 'NA'),
            'RFD Date': formatDisplayDate(row.readyToDispatchDate, 'NA'),
            'Days Difference': formatRfdDaysDifference(row, 'NA'),
            'Current Stage': row.currentStage || '',
            'City Name': row.cityName || '',
            'Beam Saw': stageValue(row, 'Beam saw'),
            'Edge Banding': stageValue(row, 'Edge banding'),
            CNC: stageValue(row, 'CNC'),
            'Alu Panel': stageValue(row, 'Alu Panels'),
            'CNC Jali': stageValue(row, 'CNC Jali'),
            'Custom Panel': stageValue(row, 'Custom Panels'),
            QC: stageValue(row, 'Assembly'),
            Packaging: stageValue(row, 'Packaging'),
            'Ready for Dispatch': stageValue(row, 'Completed'),
            Delivered: stageValue(row, 'Delivered'),
            'Total Panels': Number(row.totalPanels || 0),
            'Payment Status': getPaymentStatus(row),
            'SignOff Amount': Number(row.staticDesignWonAmount || 0),
            'Received Amount': Number(row.totalAmountReceived || 0),
            'Received %': getReceivedPercentage(row),
            'Unit Name': formatUnitNames(row),
            'Expected Material Readiness Date': formatDisplayDate(row.materialReadinessDate, 'NA'),
        }));

        csvRows.push({
            SN: '',
            'Project No': `Total (${rows.length})`,
            'Created Date': '',
            'First Beam Scan Date': '',
            'RFD Date': '',
            'Days Difference': '',
            'Current Stage': '',
            'City Name': '',
            'Beam Saw': totals.beamSaw,
            'Edge Banding': totals.edgeBanding,
            CNC: totals.cnc,
            'Alu Panel': totals.aluPanel,
            'CNC Jali': totals.cncJali,
            'Custom Panel': totals.customPanel,
            QC: totals.qc,
            Packaging: totals.packaging,
            'Ready for Dispatch': totals.readyForDispatch,
            Delivered: totals.deliveredProductPanel,
            'Total Panels': totals.totalPanels,
            'Payment Status': '',
            'SignOff Amount': '',
            'Received Amount': '',
            'Received %': '',
            'Unit Name': '',
            'Expected Material Readiness Date': '',
        });

        const worksheet = XLSX.utils.json_to_sheet(csvRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Production Summary');
        XLSX.writeFile(workbook, `Production_Summary_Project_${new Date().toISOString().slice(0, 10)}.csv`, { bookType: 'csv' });
    };

    const downloadPanelExcel = async () => {
        if (!panelMeta.count) {
            props.setSnackInfo('No panel data to download', 'warning');
            return;
        }

        const fileName = `${panelMeta.projectNo || 'Production'}_${panelMeta.stageLabel || panelMeta.stage}_panels.csv`;
        const payload = {
            stage: panelMeta.stage,
            fileName,
        };

        if (panelMeta.projectIds?.length) {
            payload.projectIds = panelMeta.projectIds;
        } else if (panelMeta.projectId) {
            payload.projectId = panelMeta.projectId;
        } else {
            props.setSnackInfo('Missing project details for download', 'error');
            return;
        }

        setPanelDownloadProgress(0);
        setPanelDownloadOpen(true);

        try {
            const res = await downloadProductionSummaryPanelCsv({
                payload,
                totalCount: panelMeta.count,
                onProgress: setPanelDownloadProgress,
            });
            saveCsvBlob(res.data, fileName);
            props.setSnackInfo(`Downloaded ${panelMeta.count.toLocaleString()} panel records`, 'success');
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data?.error || 'Failed to download panel details',
                'error'
            );
        } finally {
            setPanelDownloadOpen(false);
            setPanelDownloadProgress(0);
        }
    };

    const renderCountCell = (row, column) => {
        const count = column.getCount(row);
        if (!count || !row._id) {
            return <span className={classes.disabledCount}>{count || 0}</span>;
        }
        return (
            <span
                className={classes.clickableCount}
                onClick={() => openPanelDetails(row, column)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openPanelDetails(row, column);
                }}
            >
                {count}
            </span>
        );
    };

    const renderTotalCountCell = (column) => {
        const count = totals[column.key];
        if (!count) {
            return <span className={classes.disabledCount}>0</span>;
        }
        return (
            <span
                className={classes.clickableCount}
                onClick={() => openTotalPanelDetails(column)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openTotalPanelDetails(column);
                }}
            >
                {count}
            </span>
        );
    };

    const renderDaysDifferenceCell = (row) => {
        const days = getRfdDaysDifference(row);
        const label = days == null ? 'NA' : days;
        const tone = getRfdDaysDifferenceTone(days);
        const toneClass = {
            green: classes.daysDifferenceGreen,
            orange: classes.daysDifferenceOrange,
            red: classes.daysDifferenceRed,
        }[tone];

        return (
            <StyledTableCell className={`${classes.daysDifferenceCell} ${toneClass || ''}`.trim()}>
                {label}
            </StyledTableCell>
        );
    };

    const renderPaymentStatusCell = (row) => {
        const status = getPaymentStatus(row);
        const percentage = getReceivedPercentage(row);
        const chipClassName = {
            [PAYMENT_STATUS.NOT_PAID]: classes.paymentStatusNotPaid,
            [PAYMENT_STATUS.PARTIAL]: classes.paymentStatusPartial,
            [PAYMENT_STATUS.PAID]: classes.paymentStatusPaid,
        }[status];

        return (
            <span className={classes.paymentStatusCell}>
                <span className={`${classes.paymentStatusChip} ${chipClassName}`}>
                    {status}
                </span>
                <Tooltip
                    arrow
                    title={(
                        <>
                            <div className={classes.paymentTooltipLine}>
                                SignOff Amount: {formatCurrency(row.staticDesignWonAmount)}
                            </div>
                            <div className={classes.paymentTooltipLine}>
                                Received Amount: {formatCurrency(row.totalAmountReceived)}
                            </div>
                            <div className={classes.paymentTooltipLine}>
                                Received %: {percentage.toFixed(2)}%
                            </div>
                        </>
                    )}
                >
                    <VisibilityOutlinedIcon className={classes.paymentViewIcon} />
                </Tooltip>
            </span>
        );
    };

    const renderUnitNameCell = (row) => {
        const unitNames = formatUnitNames(row);
        const tooltipText = formatUnitWiseTooltip(row);

        if (!tooltipText || unitNames === 'NA') {
            return unitNames;
        }

        return (
            <Tooltip
                arrow
                title={(
                    <>
                        {tooltipText.split('\n').map((line) => (
                            <div key={line} className={classes.paymentTooltipLine}>{line}</div>
                        ))}
                    </>
                )}
            >
                <span>{unitNames}</span>
            </Tooltip>
        );
    };

    const handleMaterialReadinessDraftChange = (leadId, value) => {
        setMaterialReadinessDrafts((prev) => ({
            ...prev,
            [leadId]: value,
        }));
    };

    const handleMaterialReadinessSubmit = async (row) => {
        const leadId = row?.leadId;
        if (!leadId || isMaterialReadinessLocked(row)) {
            return;
        }

        const draftDate = materialReadinessDrafts[leadId];
        if (!draftDate) {
            props.setSnackInfo('Please select an expected material readiness date', 'warning');
            return;
        }

        if (draftDate <= moment().format('YYYY-MM-DD')) {
            props.setSnackInfo('Expected material readiness date must be after today', 'warning');
            return;
        }

        setMaterialReadinessSubmitting((prev) => ({ ...prev, [leadId]: true }));
        try {
            const res = await HttpService.setMaterialReadinessDate({
                leadId,
                materialReadinessDate: draftDate,
            });
            const savedDate = res.data?.materialReadinessDate || draftDate;
            setAllRows((prev) => prev.map((item) => (
                String(item.leadId) === String(leadId)
                    ? {
                        ...item,
                        materialReadinessDate: savedDate,
                        isEditable: false,
                    }
                    : item
            )));
            setMaterialReadinessDrafts((prev) => {
                const next = { ...prev };
                delete next[leadId];
                return next;
            });
            props.setSnackInfo('Expected material readiness date saved', 'success');
        } catch (error) {
            props.setSnackInfo(
                error?.response?.data?.message || error?.response?.data?.error || 'Failed to save expected material readiness date',
                'error'
            );
        }
        setMaterialReadinessSubmitting((prev) => {
            const next = { ...prev };
            delete next[leadId];
            return next;
        });
    };

    const renderMaterialReadinessCell = (row) => {
        if (isMaterialReadinessLocked(row)) {
            return (
                <span className={classes.materialReadinessSavedDate}>
                    {formatDisplayDate(row.materialReadinessDate, 'NA')}
                </span>
            );
        }

        if (!isFactoryManager) {
            return 'NA';
        }

        const leadId = row?.leadId;
        if (!leadId) {
            return 'NA';
        }

        const isSubmitting = materialReadinessSubmitting[leadId];
        const draftDate = materialReadinessDrafts[leadId] || '';

        return (
            <Box className={classes.materialReadinessCell}>
                <TextField
                    className={classes.materialReadinessDateField}
                    size="small"
                    variant="outlined"
                    type="date"
                    value={draftDate}
                    disabled={isSubmitting}
                    onChange={(e) => handleMaterialReadinessDraftChange(leadId, e.target.value)}
                    inputProps={{ min: MATERIAL_READINESS_MIN_DATE }}
                    InputLabelProps={{ shrink: true }}
                />
                <Button
                    className={classes.materialReadinessSubmitBtn}
                    variant="contained"
                    disableElevation
                    disabled={isSubmitting || !draftDate}
                    onClick={() => handleMaterialReadinessSubmit(row)}
                    startIcon={isSubmitting ? <CircularProgress size={14} color="inherit" /> : <CheckIcon fontSize="small" />}
                >
                    {isSubmitting ? 'Saving' : 'Save'}
                </Button>
            </Box>
        );
    };

    return (
        <div className={classes.root}>
            <Box className={classes.pageHeader}>
                <Typography variant="h5" className={classes.title}>Production Summary Project</Typography>
                <Typography variant="body2" className={classes.subtitle}>
                    Tracking Tool Panel Count
                </Typography>
            </Box>

            <Paper className={classes.filterPaper} elevation={0}>
                <Box className={classes.filterRowTop}>
                    <TextField
                        className={`${classes.filterField} ${classes.topRowItem}`}
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Created From"
                        type="date"
                        value={createdStartDate}
                        onChange={(e) => setCreatedStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        className={`${classes.filterField} ${classes.topRowItem}`}
                        fullWidth
                        size="small"
                        variant="outlined"
                        label="Created To"
                        type="date"
                        value={createdEndDate}
                        onChange={(e) => setCreatedEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        className={`${classes.filterField} ${classes.topRowItem}`}
                        fullWidth
                        size="small"
                        variant="outlined"
                        select
                        label="City"
                        value={selectedCities}
                        onChange={handleCityChange}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                selected.includes(ALL_CITIES) || !selected.length
                                    ? ALL_CITIES
                                    : formatMultiSelectLabel(selected, ALL_CITIES)
                            ),
                        }}
                    >
                        <MenuItem value={ALL_CITIES}>
                            <Checkbox size="small" checked={selectedCities.includes(ALL_CITIES)} />
                            <ListItemText primary={ALL_CITIES} />
                        </MenuItem>
                        {cityOptions.map((city) => (
                            <MenuItem key={city} value={city}>
                                <Checkbox size="small" checked={selectedCities.indexOf(city) > -1} />
                                <ListItemText primary={city} />
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        className={`${classes.filterField} ${classes.topRowItem}`}
                        fullWidth
                        size="small"
                        variant="outlined"
                        select
                        label="Current Stage"
                        value={selectedStages}
                        onChange={handleCurrentStageChange}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                selected.includes(ALL_STAGES) || !selected.length
                                    ? ALL_STAGES
                                    : formatMultiSelectLabel(selected, ALL_STAGES)
                            ),
                        }}
                    >
                        <MenuItem value={ALL_STAGES}>
                            <Checkbox size="small" checked={selectedStages.includes(ALL_STAGES)} />
                            <ListItemText primary={ALL_STAGES} />
                        </MenuItem>
                        {CURRENT_STAGE_ORDER.map((stage) => (
                            <MenuItem key={stage} value={stage}>
                                <Checkbox size="small" checked={selectedStages.indexOf(stage) > -1} />
                                <ListItemText primary={stage} />
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        className={`${classes.filterField} ${classes.topRowItem}`}
                        fullWidth
                        size="small"
                        variant="outlined"
                        select
                        label="Payment Status"
                        value={selectedPaymentStatuses}
                        onChange={handlePaymentStatusChange}
                        SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                                selected.includes(ALL_PAYMENT_STATUSES) || !selected.length
                                    ? ALL_PAYMENT_STATUSES
                                    : formatMultiSelectLabel(selected, ALL_PAYMENT_STATUSES)
                            ),
                        }}
                    >
                        <MenuItem value={ALL_PAYMENT_STATUSES}>
                            <Checkbox size="small" checked={selectedPaymentStatuses.includes(ALL_PAYMENT_STATUSES)} />
                            <ListItemText primary={ALL_PAYMENT_STATUSES} />
                        </MenuItem>
                        {PAYMENT_STATUS_OPTIONS.map((status) => (
                            <MenuItem key={status} value={status}>
                                <Checkbox size="small" checked={selectedPaymentStatuses.indexOf(status) > -1} />
                                <ListItemText primary={status} />
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button
                        className={`${classes.downloadBtn} ${classes.topRowDownloadBtn} ${classes.topRowItem}`}
                        variant="contained"
                        fullWidth
                        startIcon={<GetAppOutlinedIcon />}
                        disabled={!rows.length}
                        onClick={downloadSummaryCsv}
                    >
                        Download CSV
                    </Button>
                </Box>

                <Box className={`${classes.filterRowSecondary} ${classes.searchRowGroup}`}>
                    <TextField
                        className={`${classes.filterField} ${classes.searchTypeField}`}
                        size="small"
                        variant="outlined"
                        select
                        label="Search Type"
                        value={searchType}
                        onChange={(e) => setSearchType(e.target.value)}
                    >
                        {SEARCH_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        className={`${classes.filterField} ${classes.search}`}
                        size="small"
                        variant="outlined"
                        label="Search"
                        placeholder="Enter project no, lead no, etc."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                    />
                    <Box className={classes.filterActions}>
                        <Button
                            className={classes.actionBtn}
                            variant="contained"
                            onClick={handleSearchSubmit}
                        >
                            Submit
                        </Button>
                        <Button
                            className={classes.actionBtn}
                            variant="contained"
                            onClick={handleClearFilter}
                        >
                            Clear Filter
                        </Button>
                        <Button
                            className={isFetchDisabled ? classes.disabledActionBtn : classes.actionBtn}
                            variant="contained"
                            onClick={handleFetchNewData}
                            disabled={isFetchDisabled}
                        >
                            {isFetchDisabled ? `Wait (${formatFetchTimeLeft()})` : 'Fetch New Data'}
                        </Button>
                        <Typography className={classes.totalProjects} component="span">
                            Total Projects: <strong>{rows.length}</strong>
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell className={classes.stickySnHead}>SN</StyledTableCell>
                            <StyledTableCell className={classes.stickyProjectHead}>Project No</StyledTableCell>
                            <StyledTableCell>
                                <Tooltip title="Project Created in Tracking tool" arrow>
                                    <span className={classes.headerHint}>
                                        Created Date
                                        <span className={classes.helpMark}>?</span>
                                    </span>
                                </Tooltip>
                            </StyledTableCell>
                            <StyledTableCell>
                                <Tooltip title="First Beam Saw scan date in Tracking Tool" arrow>
                                    <span className={classes.headerHint}>
                                        First Beam Scan Date
                                        <span className={classes.helpMark}>?</span>
                                    </span>
                                </Tooltip>
                            </StyledTableCell>
                            <StyledTableCell>
                                <Tooltip title="Ready for Dispatch date" arrow>
                                    <span className={classes.headerHint}>
                                        RFD Date
                                        <span className={classes.helpMark}>?</span>
                                    </span>
                                </Tooltip>
                            </StyledTableCell>
                            <StyledTableCell>
                                <Tooltip title="Days between First Beam Scan Date and RFD Date" arrow>
                                    <span className={classes.headerHint}>
                                        Days Difference
                                        <span className={classes.helpMark}>?</span>
                                    </span>
                                </Tooltip>
                            </StyledTableCell>
                            <StyledTableCell>Current Stage</StyledTableCell>
                            <StyledTableCell>City Name</StyledTableCell>
                            {STAGE_COLUMNS.map((col) => (
                                <StyledTableCell key={col.key}>{col.label}</StyledTableCell>
                            ))}
                            <StyledTableCell>Payment Status</StyledTableCell>
                            <StyledTableCell>Unit Name</StyledTableCell>
                            <StyledTableCell>Expected Material Readiness Date</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow key={`${row.uniqueLeadNumber}-${index}`}>
                                <StyledTableCell className={classes.stickySnCell}>{index + 1}</StyledTableCell>
                                <StyledTableCell className={classes.stickyProjectCell}>
                                    {(() => {
                                        const projectNo = row.uniqueLeadNumber || row.project_no || '—';
                                        const logId = row.leadId || row._id;
                                        if (!logId || projectNo === '—') {
                                            return projectNo;
                                        }
                                        return (
                                            <Link
                                                to={`/project-logs/${logId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={classes.projectNoLink}
                                            >
                                                {projectNo}
                                            </Link>
                                        );
                                    })()}
                                </StyledTableCell>
                                <StyledTableCell>{formatDisplayDate(row.createdAt, 'NA')}</StyledTableCell>
                                <StyledTableCell>{formatDisplayDate(row.firstBeamScanDate, 'NA')}</StyledTableCell>
                                <StyledTableCell>{formatDisplayDate(row.readyToDispatchDate, 'NA')}</StyledTableCell>
                                {renderDaysDifferenceCell(row)}
                                <StyledTableCell>{row.currentStage || '—'}</StyledTableCell>
                                <StyledTableCell>{row.cityName || '—'}</StyledTableCell>
                                {STAGE_COLUMNS.map((col) => (
                                    <StyledTableCell key={col.key}>
                                        {renderCountCell(row, col)}
                                    </StyledTableCell>
                                ))}
                                <StyledTableCell>{renderPaymentStatusCell(row)}</StyledTableCell>
                                <StyledTableCell>{renderUnitNameCell(row)}</StyledTableCell>
                                <StyledTableCell>{renderMaterialReadinessCell(row)}</StyledTableCell>
                            </TableRow>
                        ))}

                        {rows.length > 0 && (
                            <TableRow className={classes.totalRow}>
                                <StyledTableCell colSpan={TABLE_META_COLUMNS}>Total ({rows.length})</StyledTableCell>
                                {STAGE_COLUMNS.map((col) => (
                                    <StyledTableCell key={col.key}>
                                        {renderTotalCountCell(col)}
                                    </StyledTableCell>
                                ))}
                                <StyledTableCell>—</StyledTableCell>
                                <StyledTableCell>—</StyledTableCell>
                                <StyledTableCell>—</StyledTableCell>
                            </TableRow>
                        )}

                        {!rows.length && (
                            <TableRow>
                                <StyledTableCell colSpan={TABLE_META_COLUMNS + STAGE_COLUMNS.length + SUMMARY_EXTRA_COLUMNS}>
                                    {activeSearch || (selectedCities.length && !selectedCities.includes(ALL_CITIES))
                                        || (selectedStages.length && !selectedStages.includes(ALL_STAGES))
                                        || (selectedPaymentStatuses.length && !selectedPaymentStatuses.includes(ALL_PAYMENT_STATUSES))
                                        || appliedCreatedStartDate || appliedCreatedEndDate
                                        ? 'No projects found for selected filters.'
                                        : 'No production summary projects found.'}
                                </StyledTableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={panelDialogOpen}
                onClose={() => setPanelDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    Panel Details — {panelMeta.projectNo} ({panelMeta.stageLabel})
                </DialogTitle>
                <DialogContent dividers>
                    <Typography className={classes.dialogMeta} variant="body2">
                        Stage: <strong>{panelMeta.stageLabel}</strong>
                        {' | '}
                        Panels: <strong>{panelMeta.count.toLocaleString()}</strong>
                        {panelMeta.isPreview && (
                            <>
                                {' | '}
                                Showing first <strong>{panelMeta.previewCount.toLocaleString()}</strong> in table.
                                {' '}
                                Use download for all records.
                            </>
                        )}
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {PANEL_COLUMNS.map((col) => (
                                        <StyledTableCell key={col.key}>{col.label}</StyledTableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {panelRows.map((panel, index) => (
                                    <TableRow key={panel._id || `${panel.barcode}-${index}`}>
                                        <StyledTableCell>{index + 1}</StyledTableCell>
                                        <StyledTableCell>{panel.barcode || '—'}</StyledTableCell>
                                        <StyledTableCell>{panel.name1 || '—'}</StyledTableCell>
                                        <StyledTableCell>{panel.matname || '—'}</StyledTableCell>
                                        <StyledTableCell>{panel.cp90name || '—'}</StyledTableCell>
                                        <StyledTableCell>{formatPanelStageDisplay(panel.stage, panel) || '—'}</StyledTableCell>
                                        <StyledTableCell>{panel.space_type || '—'}</StyledTableCell>
                                        <StyledTableCell>{panel.rleng ?? '—'}</StyledTableCell>
                                        <StyledTableCell>{panel.rwidth ?? '—'}</StyledTableCell>
                                        <StyledTableCell>{panel.rthk ?? '—'}</StyledTableCell>
                                    </TableRow>
                                ))}
                                {!panelRows.length && (
                                    <TableRow>
                                        <StyledTableCell colSpan={PANEL_COLUMNS.length}>
                                            No panels found for this stage.
                                        </StyledTableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPanelDialogOpen(false)}>Close</Button>
                    <Button
                        className={classes.downloadBtn}
                        variant="contained"
                        onClick={downloadPanelExcel}
                        disabled={!panelMeta.count || panelDownloadOpen}
                    >
                        {panelMeta.isPreview ? 'Download All CSV' : 'Download CSV'}
                    </Button>
                </DialogActions>
            </Dialog>

            <PanelDownloadProgressDialog
                open={panelDownloadOpen}
                progress={panelDownloadProgress}
                totalCount={panelMeta.count}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    productionSummaryProjects: state.productionSummaryProject.projects,
    productionSummaryCityOptions: state.productionSummaryProject.cityOptions,
    isProductionSummaryLoaded: state.productionSummaryProject.isLoaded,
    userDetails: state.user.userDetails,
});

export default connect(mapStateToProps, {
    setLoading,
    setSnackInfo,
    setProductionSummaryProjects,
})(ProductionSummaryProject);
